<template>
  <RevForm
    :id="id"
    class="mt-12"
    :initial-values="initialValues"
    :name="id"
    :validate="validate"
    @submit="handleSubmit"
  >
    <template #default="{ values, errors }">
      <div ref="container">
        <ProcessOutInput
          :disabled="props.disabled"
          v-bind="virtualForm['cc-number']"
          :label="i18n(translations.numberLabel)"
        >
          <template #icon>
            <PaymentNetworkRadioGroup
              v-model="selectedNetwork"
              :disabled="props.disabled"
              :networks="[
                /* TODO: [PAYIN-3147] */
              ]"
              :preferred-networks="preferredNetworks"
            />
          </template>
        </ProcessOutInput>

        <div class="mt-12 flex gap-12">
          <ProcessOutInput
            :disabled="props.disabled"
            v-bind="virtualForm['cc-exp']"
            :icon="IconCalendar"
            :label="i18n(translations.dateLabel)"
          />

          <ProcessOutInput
            :disabled="props.disabled"
            v-bind="virtualForm['cc-cvc']"
            :icon="IconLock"
            input-id="cc-cvc"
            :label="i18n(translations.securityCodeLabel)"
          />
        </div>

        <div class="mt-12 flex justify-end gap-12">
          <InputCreditCardName
            v-model="values.name"
            class="w-full"
            :disabled="disabled"
            :error="errors.name || virtualForm['name'].error"
          />
        </div>
      </div>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { paymentAPI } from '@backmarket/http-api'
import { PaymentNetwork } from '@backmarket/http-api/src/api-specs-payment/payment'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevForm, makeValidate } from '@ds/components/Form'
import { IconCalendar } from '@ds/icons/IconCalendar'
import { IconLock } from '@ds/icons/IconLock'

import { InputCreditCardNameValidators } from '../../../form-common/components/InputCreditCardName.utils'
import InputCreditCardName from '../../../form-common/components/InputCreditCardName.vue'
import PaymentNetworkRadioGroup from '../../../form-common/components/PaymentNetworkRadioGroup/PaymentNetworkRadioGroup.vue'
import { getRedirectionToPaymentResult } from '../../../form-common/helpers/getRedirection'
import {
  PaymentError,
  type PaymentFormAdapterEmits,
  type PaymentFormAdapterProps,
  PaymentSetupError,
  PaymentSubmitError,
} from '../../../form-common/types'
import { useProcessOutForm } from '../../composables/useProcessOutForm'
import { getExceptionReadableMessage } from '../../helpers/getExceptionReadableMessage'
import { isProcessOutException } from '../../helpers/isProcessOutException'

import translations from './ProcessOutCardAdapter.translations'
import ProcessOutInput from './ProcessOutInput.vue'

const props = defineProps<PaymentFormAdapterProps>()
const emit = defineEmits<PaymentFormAdapterEmits>()

type Values = {
  name: string
}
const initialValues: Values = {
  name: '',
}

const i18n = useI18n()

const validate = makeValidate<Values>({
  name: InputCreditCardNameValidators(i18n),
})

const publicKey = computed(() => {
  const config = props.paymentMethod?.config ?? {}

  return 'publicKey' in config ? config.publicKey : null
})

const preferredNetworks = computed(() => {
  const config = props.paymentMethod?.config ?? {}

  return ('preferredNetworks' in config ? config.preferredNetworks : []) ?? []
})

const { setupForm, virtualForm, tokenizeForm, makeCardPayment } =
  useProcessOutForm()
const container = ref<HTMLElement | null>(null)

onMounted(async () => {
  if (container.value === null) {
    throw new Error('PO container not found')
  }

  emit('setup-start')

  try {
    if (publicKey.value === null) {
      throw new PaymentError('PO public key not found', {
        type: '/errors/payment/misconfigured-payment-method',
      })
    }

    await setupForm(publicKey.value, container.value)

    emit('setup-success')
  } catch (error) {
    const setupError = PaymentSetupError.fromAnyError(error)

    if (isProcessOutException(error)) {
      setupError.readableMessage = getExceptionReadableMessage(i18n, error)
      setupError.source = 'FRONT_LIBRARY_PROCESSOUT'
    }

    emit('setup-error', setupError)
  }
})

const handleSubmit = async (values: Values) => {
  emit('submit-start')

  try {
    const token = await tokenizeForm({ name: values.name })

    const { paymentId, gatewayReference: invoiceId } =
      await props.createPayment()

    if (!invoiceId) {
      throw new PaymentError(
        'No gatewayReference (invoiceId) found after creating the payment',
        {
          type: '/errors/payment/unexpected/api-response',
        },
      )
    }

    try {
      await makeCardPayment(invoiceId, token, {
        authorize_only: !props.paymentMethod?.autoCaptureEnabled,
      })
    } finally {
      // calls `paymentConfirmCard` endpoint regardless the payment it is a success or not.
      await $httpFetch(paymentAPI.postConfirmCard, {
        pathParams: { paymentId },
      })
    }

    emit('submit-success', {
      paymentId,
      redirection: getRedirectionToPaymentResult(paymentId),
    })
  } catch (error) {
    emit('submit-error', PaymentSubmitError.fromAnyError(error))
  }
}

const selectedNetwork = ref<PaymentNetwork | null>(null)
</script>
